import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, Crown, GripVertical, User, ChevronDown, HelpCircle } from "lucide-react";
import { TipoAprovacao, ModoAssinatura, UsuarioObra } from "@/hooks/useNovaObra";
import { supabase } from "@/integrations/supabase/client";

// Usuários reais do sistema (profiles visíveis pela RLS; hoje, o usuário logado).
// Também é possível adicionar participantes avulsos pelo formulário "Novo usuário".
interface UsuarioSistema { id: string; nome: string; email: string; funcao: string }

interface UsuariosObraSectionProps {
  aprovacao: {
    tipo: TipoAprovacao;
    modo?: ModoAssinatura;
    participantes: UsuarioObra[];
  };
  onAprovacaoChange: (aprovacao: {
    tipo: TipoAprovacao;
    modo?: ModoAssinatura;
    participantes: UsuarioObra[];
  }) => void;
}

interface SortableUserCardProps {
  usuario: UsuarioObra;
  index: number;
  onRemove: () => void;
  onToggleAprovador: () => void;
  isApprovalMode: boolean;
  isDraggable: boolean;
  showHierarchy: boolean;
  aprovadores: UsuarioObra[];
}

function SortableUserCard({ 
  usuario, 
  index,
  onRemove, 
  onToggleAprovador, 
  isApprovalMode, 
  isDraggable,
  showHierarchy,
  aprovadores
}: SortableUserCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: usuario.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Calcular posição apenas para aprovadores
  const posicaoAprovador = usuario.aprovador ? 
    aprovadores.findIndex(p => p.id === usuario.id) + 1 : null;

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className="relative w-44 min-h-[140px] rounded-2xl shadow-sm border p-4 pt-6 bg-card transition-all duration-200 hover:shadow-lg"
    >
      {/* Header - Badges e Controls */}
      {showHierarchy && usuario.aprovador && posicaoAprovador && (
        <div className="absolute -top-3 -left-3 z-10 w-7 h-7 bg-primary text-primary-foreground rounded-2xl grid place-items-center text-xs font-bold">
          {posicaoAprovador}º
        </div>
      )}
      
      {isDraggable && usuario.aprovador && (
        <div 
          {...attributes} 
          {...listeners} 
          className="absolute top-2 right-2 z-10 cursor-grab active:cursor-grabbing opacity-40 hover:opacity-70"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}
      
      {/* Body - Informações do usuário */}
      <div className="flex flex-col items-center text-center py-2">
        <div className="relative mb-2">
          <User className="h-8 w-8 text-muted-foreground" />
          {isApprovalMode && usuario.aprovador && (
            <Crown className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500" />
          )}
        </div>
        
        <div className="w-full">
          <p className="text-sm font-semibold truncate" title={usuario.nome}>
            {usuario.nome.split(' ')[0]}
          </p>
          {usuario.funcao && (
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2 leading-tight" title={usuario.funcao}>
              {usuario.funcao}
            </p>
          )}
        </div>
      </div>
      
      {/* Footer - Ações */}
      <div className="mt-3 flex items-center justify-center gap-2">
        {isApprovalMode && (
          <Button
            variant={usuario.aprovador ? "default" : "outline"}
            size="sm"
            onClick={onToggleAprovador}
            className="h-6 w-6 p-0 rounded-xl"
            title={usuario.aprovador ? "Remover como aprovador" : "Definir como aprovador"}
          >
            <Crown className="h-3 w-3" />
          </Button>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-6 w-6 p-0 text-destructive hover:text-destructive rounded-xl"
          title="Remover usuário"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      
    </Card>
  );
}

export function UsuariosObraSection({ aprovacao, onAprovacaoChange }: UsuariosObraSectionProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [newUserModalOpen, setNewUserModalOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({ nome: "", email: "", funcao: "" });

  // Usuários reais (profiles) para o seletor de participantes
  const [usuariosSistema, setUsuariosSistema] = useState<UsuarioSistema[]>([]);
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from("profiles").select("id, full_name, email");
      if (error) { console.error("UsuariosObraSection profiles:", error.message); return; }
      setUsuariosSistema(
        (data ?? []).map((p) => ({
          id: p.id,
          nome: p.full_name || p.email || "Usuário",
          email: p.email ?? "",
          funcao: "",
        }))
      );
    })();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleTipoChange = (tipo: TipoAprovacao) => {
    onAprovacaoChange({
      ...aprovacao,
      tipo,
      modo: 'sem-hierarquia',
    });
  };

  const handleModoChange = (modo: ModoAssinatura) => {
    onAprovacaoChange({
      ...aprovacao,
      modo,
    });
  };

  const handleAddUser = () => {
    if (!selectedUserId) return;
    
    const usuario = usuariosSistema.find(u => u.id === selectedUserId);
    if (!usuario) return;
    
    const exists = aprovacao.participantes.find(p => p.id === usuario.id);
    if (exists) return;
    
    const newParticipante: UsuarioObra = {
      id: usuario.id,
      nome: usuario.nome,
      email: usuario.email,
      funcao: usuario.funcao,
      aprovador: false,
    };
    
    onAprovacaoChange({
      ...aprovacao,
      participantes: [...aprovacao.participantes, newParticipante],
    });
    
    setSelectedUserId("");
    setUserSearchOpen(false);
  };

  const handleCreateUser = () => {
    if (!newUserData.nome || !newUserData.email) return;
    
    const newId = `new_${Date.now()}`;
    const newParticipante: UsuarioObra = {
      id: newId,
      nome: newUserData.nome,
      email: newUserData.email,
      funcao: newUserData.funcao,
      aprovador: false,
    };
    
    onAprovacaoChange({
      ...aprovacao,
      participantes: [...aprovacao.participantes, newParticipante],
    });
    
    setNewUserData({ nome: "", email: "", funcao: "" });
    setNewUserModalOpen(false);
  };

  const handleRemoveUser = (id: string) => {
    onAprovacaoChange({
      ...aprovacao,
      participantes: aprovacao.participantes.filter(p => p.id !== id),
    });
  };

  const handleToggleAprovador = (id: string) => {
    const newAprovacao = ((prev: typeof aprovacao) => {
      const items = [...prev.participantes];
      const idx = items.findIndex(u => u.id === id);
      if (idx < 0) return prev;

      const u = { ...items[idx] };
      const aprovadores = items.filter(i => i.aprovador);
      const naoAprovadores = items.filter(i => !i.aprovador);

      if (!u.aprovador) {
        // vira aprovador → vai para o FINAL do bloco de aprovadores
        u.aprovador = true;
        // remover de não-aprovadores
        const semU = naoAprovadores.filter(i => i.id !== u.id);
        const novosAprov = [...aprovadores.filter(i => i.id !== u.id), u]; // push no fim
        return { 
          ...prev, 
          participantes: [...novosAprov, ...semU] 
        };
      } else {
        // deixa de ser aprovador → sai do bloco e vai para não-aprovadores (final do bloco)
        u.aprovador = false;
        const novosAprov = aprovadores.filter(i => i.id !== u.id);
        const novosNaoAprov = [...naoAprovadores.filter(i => i.id !== u.id), u];
        return { 
          ...prev, 
          participantes: [...novosAprov, ...novosNaoAprov] 
        };
      }
    })(aprovacao);
    
    onAprovacaoChange(newAprovacao);
  };


  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Limitar reordenamento apenas entre aprovadores
      const aprovadores = aprovacao.participantes.filter(p => p.aprovador);
      const naoAprovadores = aprovacao.participantes.filter(p => !p.aprovador);
      
      const oldIndex = aprovadores.findIndex(p => p.id === active.id);
      const newIndex = aprovadores.findIndex(p => p.id === over.id);
      
      if (oldIndex >= 0 && newIndex >= 0) {
        const newAprovadores = [...aprovadores];
        const [reorderedItem] = newAprovadores.splice(oldIndex, 1);
        newAprovadores.splice(newIndex, 0, reorderedItem);
        
        onAprovacaoChange({
          ...aprovacao,
          participantes: [...newAprovadores, ...naoAprovadores],
        });
      }
    }
  };

  const availableUsers = usuariosSistema.filter(
    user => !aprovacao.participantes.find(p => p.id === user.id)
  );

  const isDraggable = aprovacao.modo === 'hierarquia';
  const showHierarchy = aprovacao.modo === 'hierarquia';
  
  // Separar aprovadores e não-aprovadores
  const aprovadores = aprovacao.participantes.filter(p => p.aprovador);
  const naoAprovadores = aprovacao.participantes.filter(p => !p.aprovador);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Label className="text-sm font-medium">Usuários da Obra</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-xs">
                <strong>Simples:</strong> Aprovação direta<br/>
                <strong>Assinatura:</strong> Fluxo com aprovadores e ordem
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Configuração de Aprovação */}
      <div className="space-y-3">
        <div className="flex items-start justify-start">
          {/* Tipo de Aprovação - Esquerda */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Tipo de aprovação</Label>
            <div className="flex space-x-1">
              <Button
                variant={aprovacao.tipo === 'simples' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleTipoChange('simples')}
              >
                Simples
              </Button>
              <Button
                variant={aprovacao.tipo === 'assinatura' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleTipoChange('assinatura')}
              >
                Assinatura
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1 min-h-4">
              {aprovacao.tipo === 'simples' ? 'Usuário aprova pelo sistema.' : 'Usuário deve assinar para aprovar.'}
            </div>
          </div>
          
          {/* Modo de organização - Centro */}
          <div className="space-y-1 mx-auto">
            <Label className="text-xs text-muted-foreground">Modo de organização</Label>
            <div className="flex space-x-1">
              <Button
                variant={aprovacao.modo === 'sem-hierarquia' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModoChange('sem-hierarquia')}
              >
                Sem hierarquia
              </Button>
              <Button
                variant={aprovacao.modo === 'hierarquia' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleModoChange('hierarquia')}
              >
                Com hierarquia
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1 min-h-4">
              {aprovacao.modo === 'hierarquia' ? 'Aprovação segue a ordem hierárquica.' : 'Aprovadores aprovam sem ordem definida.'}
            </div>
          </div>
        </div>
        
      </div>

      {/* Adicionar Usuários */}
      <div className="flex space-x-2">
        <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1 justify-between">
              {selectedUserId
                ? usuariosSistema.find(u => u.id === selectedUserId)?.nome
                : "Selecionar usuário"
              }
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0">
            <Command>
              <CommandInput placeholder="Buscar usuário..." />
              <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  {availableUsers.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.nome}
                      onSelect={() => {
                        setSelectedUserId(user.id);
                        setUserSearchOpen(false);
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{user.nome}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        
        <Button onClick={handleAddUser} disabled={!selectedUserId}>
          <Plus className="h-4 w-4 mr-1" />
          Adicionar
        </Button>
        
        <Dialog open={newUserModalOpen} onOpenChange={setNewUserModalOpen}>
          <DialogTrigger asChild>
            <Button variant="outline">Novo usuário</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Criar novo usuário</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={newUserData.nome}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, nome: e.target.value }))}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@empresa.com"
                />
              </div>
              <div>
                <Label htmlFor="funcao">Cargo/Função</Label>
                <Input
                  id="funcao"
                  value={newUserData.funcao}
                  onChange={(e) => setNewUserData(prev => ({ ...prev, funcao: e.target.value }))}
                  placeholder="Ex: Engenheiro Civil"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setNewUserModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateUser}
                  disabled={!newUserData.nome || !newUserData.email}
                >
                  Criar usuário
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista de Participantes */}
      {aprovacao.participantes.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground">
              Participantes ({aprovacao.participantes.length}) 
              {aprovadores.length > 0 && ` • ${aprovadores.length} aprovador${aprovadores.length > 1 ? 'es' : ''}`}
            </Label>
            {isDraggable && aprovadores.length > 0 && (
              <span className="text-xs text-muted-foreground">
                Arraste aprovadores para definir hierarquia
              </span>
            )}
            {!isDraggable && aprovacao.modo === 'sem-hierarquia' && (
              <span className="text-xs text-muted-foreground">
                Sem ordem hierárquica
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-0 max-h-48 overflow-y-auto p-3 border rounded-xl bg-muted/20">
            {isDraggable ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={aprovadores.map(p => p.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {/* Primeiro os aprovadores (draggable) */}
                  {aprovadores.map((usuario, index) => (
                    <SortableUserCard
                      key={usuario.id}
                      usuario={usuario}
                      index={index}
                      onRemove={() => handleRemoveUser(usuario.id)}
                      onToggleAprovador={() => handleToggleAprovador(usuario.id)}
                      isApprovalMode={true}
                      isDraggable={true}
                      showHierarchy={showHierarchy}
                      aprovadores={aprovadores}
                    />
                  ))}
                </SortableContext>
                
                {/* Depois os não-aprovadores (não draggable) */}
                {naoAprovadores.map((usuario, index) => (
                  <SortableUserCard
                    key={usuario.id}
                    usuario={usuario}
                    index={aprovadores.length + index}
                    onRemove={() => handleRemoveUser(usuario.id)}
                    onToggleAprovador={() => handleToggleAprovador(usuario.id)}
                    isApprovalMode={true}
                    isDraggable={false}
                    showHierarchy={showHierarchy}
                    aprovadores={aprovadores}
                  />
                ))}
              </DndContext>
            ) : (
              aprovacao.participantes.map((usuario, index) => (
                <SortableUserCard
                  key={usuario.id}
                  usuario={usuario}
                  index={index}
                  onRemove={() => handleRemoveUser(usuario.id)}
                  onToggleAprovador={() => handleToggleAprovador(usuario.id)}
                  isApprovalMode={true}
                  isDraggable={false}
                  showHierarchy={showHierarchy}
                  aprovadores={aprovadores}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}